import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import TairLogo from './TairLogo';

const Footer = () => {
  const { t, lang } = useLanguage();
  const year = new Date().getFullYear();

  const navItems = [
    { to: '/', label: t.nav.home },
    { to: '/quienes-somos', label: t.nav.about },
    { to: '/que-hacemos', label: t.nav.whatWeDo },
    { to: '/contacto', label: t.nav.contact },
  ];

  const verticals = [
    { to: '/que-hacemos/gestion-inmobiliaria', label: t.verticals.v1.title },
    { to: '/que-hacemos/inversion-inmobiliaria', label: t.verticals.v2.title },
    { to: '/que-hacemos/bodegaje-industrial', label: t.verticals.v3.title },
    { to: '/que-hacemos/desarrollo-software', label: t.verticals.v4.title },
  ];

  return (
    <footer className="relative mt-24 md:mt-32 border-t border-white/[0.06] bg-[#050912] overflow-hidden" data-testid="site-footer">
      {/* Top gold rule */}
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />

      {/* Editorial big-word finish */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 md:pt-32 pb-12">
        <div className="flex items-baseline gap-4 flex-wrap">
          <span className="edge-number">§ Sign-off</span>
        </div>
        <h2
          className="mt-6 font-display font-extrabold tracking-tight text-white leading-[0.9]"
          style={{ fontSize: 'clamp(4rem, 14vw, 14rem)' }}
          data-testid="footer-tagline"
        >
          {lang === 'en' ? 'Bringing' : 'Va a traer'}
          <br />
          <span className="font-serif-i text-amber-300" style={{ fontWeight: 400 }}>
            {lang === 'en' ? 'light.' : 'luz.'}
          </span>
        </h2>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 border-t border-white/[0.06] pt-14">
          <div className="md:col-span-4">
            <TairLogo size="lg" />
            <p className="mt-6 max-w-sm font-body text-white/55 text-base leading-relaxed">
              {t.footer.tagline}
              <br />
              <span className="text-white/35 text-sm font-mono">Inversiones Tair SpA · Chile · Est. 2012</span>
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/70 mb-5">
              — {t.footer.navTitle}
            </h4>
            <ul className="space-y-2.5">
              {navItems.map((item, i) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="group inline-flex items-center gap-2 text-white/65 hover:text-amber-300 text-sm transition-colors"
                    data-testid={`footer-link-${item.to}`}
                  >
                    <span className="font-mono text-[10px] text-white/30">0{i + 1}</span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/70 mb-5">
              — {t.footer.verticalsTitle}
            </h4>
            <ul className="space-y-2.5">
              {verticals.map((item, i) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="group inline-flex items-center gap-2 text-white/65 hover:text-amber-300 text-sm transition-colors"
                    data-testid={`footer-vertical-${item.to}`}
                  >
                    <span className="font-mono text-[10px] text-white/30">0{i + 1}</span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/70 mb-5">
              — {t.footer.contactTitle}
            </h4>
            <ul className="space-y-3 text-sm text-white/65">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-amber-300/80 flex-shrink-0" />
                <span>Román Díaz 300, Of. 101<br />Providencia, Santiago</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 mt-0.5 text-amber-300/80 flex-shrink-0" />
                <a href="mailto:amgp2707@gmail.com" className="hover:text-amber-300 inline-flex items-center gap-1" data-testid="footer-email">
                  amgp2707@gmail.com
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 mt-0.5 text-amber-300/80 flex-shrink-0" />
                <a href="tel:+56222353000" className="hover:text-amber-300" data-testid="footer-phone">+56 2 2235 3000</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] text-white/40 font-mono uppercase tracking-widest">
          <p>© {year} Inversiones Tair SpA · tair.site</p>
          <p className="flex items-center gap-3">
            <span>{t.footer.rights}</span>
            <span className="star-divider" />
            <span>{t.footer.legal}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
